import { map, reduce } from 'ramda';

import ooeConstants from '../constants';
import { IndexedNutrition, NutritionResponse } from '../types/nutrition';
import { formatItemName } from '../util/format';
import { indexBy, indexByTag } from '../util/utils';
import { ensureValueIsWholeNumber } from '../util/validate';
import { Category, ItemGroups, Menu, MenuItem, MenuResponse } from './types';

export const setGuestCountToPaperGoodsMax = (guestCount: string): string =>
  ensureValueIsWholeNumber(guestCount) > ooeConstants.PAPER_GOODS_MAX_QUANTITY
    ? `${ooeConstants.PAPER_GOODS_MAX_QUANTITY}`
    : guestCount;

const lookUpItemGroup = (itemGroupId: number = 0, itemGroups: ItemGroups) => {
  return itemGroups[itemGroupId]?.items || [];
};

function mapMenuItems(items: MenuItem[], itemGroups: ItemGroups): MenuItem[] {
  const mappedResults = map((item: MenuItem): MenuItem[] => {
    let foundItems = lookUpItemGroup(item.itemGroupId, itemGroups || { items: [] });
    if (item.disabled && item.objectType === 'MODIFIER') {
      return [];
    }
    if (
      ((item.itemPrice >= 0.1 ||
        item.objectType === 'ITEM' ||
        item.objectType === 'MODIFIER' ||
        item.objectType === 'MODIFIER_GROUPING') &&
        (item.meal || item?.objectType !== 'ITEM_GROUPING')) ||
      item.tag === 'PREMIUM_SIDES' ||
      item.tag === 'SIDES_PKGMEALS' ||
      item.tag === 'DESSERTS_PKGMEALS'
    ) {
    } else if (item.leadTime) {
      foundItems = foundItems.map((m) => ({
        ...m,
        leadTime: item.leadTime,
      }));
    }

    if (item.maximum) {
      foundItems = foundItems.map((m) => ({
        ...m,
        maximum: item.maximum,
      }));
    }

    if (item.modifierTypeGrouping) {
      foundItems = foundItems.map((m) => ({
        ...m,
        modifierTypeGrouping: item.modifierTypeGrouping,
      }));
    }

    let menuItems = mapMenuItems(foundItems, itemGroups || {});
    if (
      ((item.itemPrice >= 0.1 ||
        item.objectType === 'ITEM' ||
        item.objectType === 'MODIFIER' ||
        item.objectType === 'MODIFIER_GROUPING') &&
        (item.meal || item.objectType !== 'ITEM_GROUPING')) ||
      item.itemType === 'MEALS_PACKAGED' ||
      item.itemType === 'PACKAGED_MEALS_PREMIUM' ||
      item.tag === 'PREMIUM_SIDES' ||
      item.tag === 'SIDES_PKGMEALS' ||
      item.tag === 'DESSERTS_PKGMEALS'
    ) {
      let comboItems: MenuItem[] | undefined;
      let sideItems: MenuItem[] | undefined;
      let premiumSideItems: MenuItem[] | undefined;
      let dessertItems: MenuItem[] | undefined;
      let selectedSide: MenuItem | undefined;
      let selectedPremiumSide: MenuItem | undefined;
      let selectedDessert: MenuItem | undefined;
      if (item.meal) {
        comboItems = [...menuItems];

        sideItems = [];
        premiumSideItems = [];
        dessertItems = [];

        menuItems = menuItems.reduce((acc, comboItem) => {
          if (
            (comboItem.itemGroupType === 'Item' || comboItem.itemGroupType === 'Side') &&
            comboItem.items &&
            comboItem.items.length
          ) {
            if (comboItem.tag === 'PREMIUM_SIDES') {
              selectedPremiumSide =
                comboItem.items.find((sideItem) => sideItem.default) || comboItem.items[0];
            } else if (comboItem.tag === 'SIDES_PKGMEALS') {
              selectedSide = comboItem.items.find((sideItem) => sideItem.default) || comboItem.items[0];
            }
          }

          if (comboItem.itemType === 'DESSERTS_GROUP' && comboItem.items && comboItem.items.length) {
            selectedDessert = comboItem.items[0];
          }

          const comboModifiers = comboItem.items
            ? comboItem.items
                .map((comboModifier) => ({
                  ...comboModifier,
                  comboTag: comboItem.tag,
                }))
                .filter((comboMod) => {
                  if (comboMod.comboTag === 'SIDES_PKGMEALS') {
                    sideItems?.push(comboMod);
                    return false;
                  }
                  if (comboMod.comboTag === 'PREMIUM_SIDES') {
                    premiumSideItems?.push(comboMod);
                    return false;
                  }
                  if (comboMod.comboTag === 'DESSERTS_PKGMEALS') {
                    dessertItems?.push(comboMod);
                    return false;
                  }
                  return true;
                })
            : [];

          return [...acc, ...comboModifiers];
        }, [] as MenuItem[]);
      }

      let modifierTypeGrouping = item.modifierTypeGrouping;
      if (!modifierTypeGrouping && item.objectType === 'MODIFIER') {
        modifierTypeGrouping = 'Other';
      }

      return [
        {
          ...item, // top level menu item (ex. chicken sandwich, premium box meal)
          name: formatItemName(item.name),
          items: menuItems, // available modifiers (ex. sauces, dressing, multigrain bun)
          selectedSide, // default premium box meal side (ex. superfood side)
          selectedPremiumSide,
          selectedDessert, // default premium box meal dessert (ex. cookie)
          comboItems, // items that make up a combo meal (ex. sandwich, cookie, chips)
          sideItems, // available sides for a premium box meal (ex. superfood side, fruit cup)
          premiumSideItems,
          dessertItems, // available desserts for a premium box meal (ex. cookie, brownie)
          modifierTypeGrouping,
        },
      ];
    }

    return menuItems;
  }, items || []);

  return mappedResults.flat();
}

const mapMenuCategories = (categories: Category[] = [], itemGroups: ItemGroups) =>
  map<Category, Category & { items: MenuItem[] }>((cat) => ({
    ...cat,
    items: mapMenuItems(cat.items, itemGroups),
  }))(categories);

export const getCombinedMenu = (menu: Menu) => {
  return mapMenuCategories(menu.categories, menu.itemGroups);
};

export const mapMenuResponseToMenu = (res: MenuResponse): Menu => {
  const itemGroups = indexBy(res.itemGroups || [], 'itemGroupId', {
    first: true,
  });
  return { categories: res.categories ?? [], itemGroups };
};

export const indexNutritionResponse = (nutrition: NutritionResponse): IndexedNutrition => {
  return indexByTag(nutrition.items);
};

export const indexedMenuItems = (menuItems: MenuItem[]) => {
  const allIndexedItems = map((menuItem) => {
    let freeSauces: number | undefined;
    const reducedItems = reduce(
      (all, category) => {
        // if free sauces — get max, but don't add to list
        if (category.sauceType === 'Free') {
          freeSauces = category.maximum;
          return [...all];
        }
        if (category.sauceType === 'Paid') {
          const o = map((c) => ({ ...c, isSauce: true }), category.items || []);
          return [...all, ...o];
        }

        if (category.comboTag) {
          const o = map((c) => ({ ...c, comboTag: category.comboTag }), category.items || []);
          return [...all, ...o];
        }

        const items = category.items || [];
        return [...all, ...items];
      },
      [] as MenuItem[],
      menuItem.items || [],
    );
    const indexedItems = indexByTag(reducedItems);
    return { ...menuItem, indexedItems, freeSauces };
  }, menuItems);
  return indexByTag(allIndexedItems);
};
